import { SelectState as AriaKitSelectState } from "ariakit";
import React from "react";
import ReactDOM from "react-dom";
import { useWindowSize } from "../hooks/useWindowSize";
import { PropOptionRenderItem } from "../PropX";
import { SelectItem } from "./SelectItem";
import styled from "styled-components";

type AdditionalSelectItemProps = {
  state: AriaKitSelectState;
  compact: boolean;
  mono: boolean;
};

type Options = {
  items: PropOptionRenderItem[];
  props: AdditionalSelectItemProps;
  elementWidth: string;
  elementMinWidth: string;
  skip: boolean;
};

type PendingPrecalculatedItemHeightState = { status: "pending"; elementToRender: JSX.Element; key?: never };
type ComputingPrecalculatedItemHeightState = { status: "computing"; elementToRender: JSX.Element; key?: never };
type DonePrecalculatedItemHeightState = { status: "done"; heights: Map<string, number>; key: string };
type SkipPrecalculatedItemHeightState = { status: "skip"; heights?: never; key?: never };

type PrecalculatedItemHeightState =
  | PendingPrecalculatedItemHeightState
  | ComputingPrecalculatedItemHeightState
  | DonePrecalculatedItemHeightState
  | SkipPrecalculatedItemHeightState;

const calculate = async (
  items: PropOptionRenderItem[],
  props: AdditionalSelectItemProps,
  container: HTMLElement
): Promise<Map<string, number>> => {
  const result = new Map<string, number>();

  const inner = document.createElement("div");
  container.appendChild(inner);

  for (const item of items) {
    if (item.type !== "option") {
      continue;
    }

    const option = item.option;

    await new Promise<void>((resolve) => {
      ReactDOM.render(<SelectItem option={option} state={props.state} mono={props.mono} compact={props.compact} />, inner, () => {
        result.set(option.value, inner.clientHeight);

        resolve();
      });
    });
  }

  ReactDOM.unmountComponentAtNode(inner);
  container.removeChild(inner);

  return result;
};

/**
 * The container element used to calculate the height of each item in the list when
 * `UNSAFE_precalculateItemHeights` is true. It must be the same width as the popover
 * to ensure the heights are calculated correctly.
 */
const PrecalculateItemHeightContainer = styled.div<{ $width: string; $minWidth: string }>`
  all: unset;
  box-sizing: border-box;
  display: block;

  /* account for the border of the real container. */
  border: 1px solid transparent;

  /* force a scrollbar to appear if the user's OS shows them persistently (e.g. when a mouse is plugged into the computer.) */
  height: 1px;
  overflow-y: scroll;

  width: ${(props) => props.$width};
  min-width: ${(props) => props.$minWidth};
`;

/**
 * A hook that will render each select item individually to calculate the height of each.
 * Because we're using `react-window` to render the list, we need to know the height of each item
 * in order to calculate the scroll position correctly. In some situations, we can't know the height
 * of each item until it's rendered. This hook will render each item in a hidden container and calculate
 * the height. Unfortunately, as you might guess, this is pretty slow, and so it should only be used
 * when absolutely necessary.
 */
export const usePrecalculatedOuterItemHeights = (options: Options): PrecalculatedItemHeightState => {
  const ref = React.useRef<HTMLDivElement>(null);
  const currentCalculationRef = React.useRef(0);
  const elementToRender = <PrecalculateItemHeightContainer ref={ref} $width={options.elementWidth} $minWidth={options.elementMinWidth} />;

  const [state, setState] = React.useState<PrecalculatedItemHeightState>(() => ({
    status: "pending",
    elementToRender,
  }));

  /**
   * If the window is resized we need to recalculate the heights.
   */
  const windowSize = useWindowSize();

  const depListKey = options.skip
    ? ""
    : options.items
        .filter((item) => item.type === "option")
        .map((item) => item.option.value)
        .join(",");

  /**
   * Deliberately not including the state in the dependency list as we don't want to recalculate
   * the heights if the state changes.
   */
  const depList: React.DependencyList = [depListKey, options.props.compact, options.props.mono, options.skip];

  /**
   * If the options have changed, we need to reset the state to pending to recalculate the heights.
   */
  React.useLayoutEffect(() => {
    if (options.skip) {
      return;
    }

    setState({ status: "pending", elementToRender });
  }, [windowSize.innerWidth, options.elementWidth, options.elementMinWidth, ...depList]);

  React.useLayoutEffect(() => {
    /**
     * If we are skipping the calculation, we don't need to do anything.
     */
    if (options.skip) {
      return;
    }

    /**
     * If the ref is not set, we can't calculate the heights.
     */
    if (!ref.current) {
      return;
    }

    /**
     * If the status is not pending, then we're already computing or done.
     */
    if (state.status !== "pending") {
      return;
    }

    /**
     * Keep track of the current calculation so that we can ignore the results of previous calculations.
     * This could occur if a computation is started, but before it finishes, the state is reset to "pending"
     * and then back to "computing". We want to avoid the chance of setting the state to "done" with the wrong
     * results.
     */
    currentCalculationRef.current += 1;
    const calculation = currentCalculationRef.current;

    setState({ status: "computing", elementToRender });

    calculate(options.items, options.props, ref.current).then((heights) => {
      if (calculation !== currentCalculationRef.current) {
        return;
      }

      setState({ status: "done", heights, key: calculation.toString() });
    });
  }, [ref.current, state.status, ...depList]);

  if (options.skip) {
    return { status: "skip" };
  }

  return state;
};
